/**
 * GA4 이벤트 트래킹 유틸리티
 * 
 * 주요 전환 이벤트 추적:
 * - 회원가입
 * - 로그인
 * - 결제 시작/완료/실패
 * - 기능 사용 (생기부, 검사기, AI 채팅)
 * - UTM 기반 채널별 전환 추적
 */

// GA4 Measurement ID
const GA_MEASUREMENT_ID = 'G-TV2KWDS364';

// ============================================================================
// UTM 파라미터 관리 (localStorage 기반)
// ============================================================================

const UTM_STORAGE_KEY = 'whatquiz_utm';
const FIRST_UTM_STORAGE_KEY = 'whatquiz_first_utm';
const UTM_EXPIRY_DAYS = 30; // UTM 정보 유효기간 (30일)

// ============================================================================
// Meta Pixel 쿠키 (_fbc, _fbp) 읽기 — CAPI EMQ 개선용
// ============================================================================

/**
 * 브라우저 쿠키에서 Meta _fbc / _fbp 값을 읽어 반환.
 * Pixel이 자동으로 심는 first-party 쿠키로, CAPI 전송 시 함께 보내면 EMQ 향상.
 */
export const getMetaCookies = () => {
  if (typeof document === 'undefined') return { fbc: null, fbp: null };
  const cookies = document.cookie.split(';').reduce((acc, c) => {
    const [k, ...v] = c.trim().split('=');
    acc[k] = v.join('=');
    return acc;
  }, {});
  return { fbc: cookies['_fbc'] || null, fbp: cookies['_fbp'] || null };
};

/**
 * CAPI 전송에 필요한 Meta 매칭 파라미터 일괄 반환.
 * API body에 spread 해서 사용: { ...getMetaMatchParams(), ... }
 */
export const getMetaMatchParams = () => {
  const { fbc, fbp } = getMetaCookies();
  return { fbc, fbp };
};

/**
 * URL에서 UTM 파라미터 추출
 */
export const extractUtmFromUrl = () => {
  if (typeof window === 'undefined') return null;
  
  const params = new URLSearchParams(window.location.search);
  const utmParams = {};
  
  const utmKeys = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'];
  
  utmKeys.forEach(key => {
    const value = params.get(key);
    if (value) {
      utmParams[key] = value;
    }
  });
  
  // UTM 파라미터가 하나라도 있으면 반환
  return Object.keys(utmParams).length > 0 ? utmParams : null;
};

/**
 * UTM 파라미터를 localStorage에 저장
 * 최신 UTM(last-touch)은 항상 덮어쓰고, first_utm은 최초 1회만 저장
 */
export const saveUtmToStorage = (utmParams) => {
  if (typeof window === 'undefined' || !utmParams) return;
  
  const utmData = {
    ...utmParams,
    savedAt: Date.now(),
    expiresAt: Date.now() + (UTM_EXPIRY_DAYS * 24 * 60 * 60 * 1000)
  };
  
  localStorage.setItem(UTM_STORAGE_KEY, JSON.stringify(utmData));
  
  saveFirstUtm(utmParams);
};

/**
 * 최초 유입 UTM을 별도로 저장 (덮어쓰지 않음 — first-touch attribution)
 */
export const saveFirstUtm = (utmParams) => {
  if (typeof window === 'undefined' || !utmParams) return;
  
  try {
    const existing = localStorage.getItem(FIRST_UTM_STORAGE_KEY);
    if (existing) {
      const parsed = JSON.parse(existing);
      if (parsed.expiresAt && Date.now() > parsed.expiresAt) {
        localStorage.removeItem(FIRST_UTM_STORAGE_KEY);
      } else {
        return; // 이미 유효한 first_utm이 있으면 덮어쓰지 않음
      }
    }
    
    const firstUtmData = {
      ...utmParams,
      landing_page: window.location.pathname,
      referrer: document.referrer || 'direct',
      first_visit: new Date().toISOString(),
      savedAt: Date.now(),
      expiresAt: Date.now() + (90 * 24 * 60 * 60 * 1000) // 90일 (first-touch는 더 길게)
    };
    
    localStorage.setItem(FIRST_UTM_STORAGE_KEY, JSON.stringify(firstUtmData));
  } catch { /* 조용히 실패 */ }
};

/**
 * 최초 유입 UTM 조회 (first-touch attribution)
 */
export const getFirstUtmFromStorage = () => {
  if (typeof window === 'undefined') return null;
  
  try {
    const stored = localStorage.getItem(FIRST_UTM_STORAGE_KEY);
    if (!stored) return null;
    
    const data = JSON.parse(stored);
    if (data.expiresAt && Date.now() > data.expiresAt) {
      localStorage.removeItem(FIRST_UTM_STORAGE_KEY);
      return null;
    }
    
    const { savedAt, expiresAt, ...utmParams } = data;
    return utmParams;
  } catch {
    return null;
  }
};

/**
 * localStorage에서 UTM 파라미터 조회
 */
export const getUtmFromStorage = () => {
  if (typeof window === 'undefined') return null;
  
  try {
    const stored = localStorage.getItem(UTM_STORAGE_KEY);
    if (!stored) return null;
    
    const utmData = JSON.parse(stored);
    
    // 만료 체크
    if (utmData.expiresAt && Date.now() > utmData.expiresAt) {
      localStorage.removeItem(UTM_STORAGE_KEY);
      return null;
    }
    
    // 메타데이터 제거하고 UTM만 반환
    const { savedAt, expiresAt, ...utmParams } = utmData;
    return utmParams;
  } catch {
    return null;
  }
};

/**
 * UTM 파라미터 초기화 (페이지 로드 시 호출)
 * - URL에 UTM이 있으면 저장
 * - 없으면 기존 저장된 값 유지
 */
export const initUtmTracking = () => {
  const urlUtm = extractUtmFromUrl();
  
  if (urlUtm) {
    saveUtmToStorage(urlUtm);
    // UTM 첫 방문 이벤트 전송
    trackUtmVisit(urlUtm);
  }
  
  return urlUtm || getUtmFromStorage();
};

/**
 * UTM 방문 이벤트 추적
 */
export const trackUtmVisit = (utmParams) => {
  if (!utmParams) return;
  
  trackEvent('utm_visit', {
    ...utmParams,
    landing_page: window.location.pathname,
    event_category: 'acquisition'
  });
};

/**
 * GA4 이벤트 전송 (UTM 정보 자동 포함)
 * @param {string} eventName - 이벤트 이름
 * @param {object} params - 이벤트 파라미터
 */
export const trackEvent = (eventName, params = {}) => {
  if (typeof window !== 'undefined' && window.gtag) {
    try {
      // 저장된 UTM 정보 가져오기
      const utmParams = getUtmFromStorage() || {};
      
      window.gtag('event', eventName, {
        ...params,
        ...utmParams,  // UTM 파라미터 자동 포함
        timestamp: new Date().toISOString()
      });
    } catch (error) {
      console.error('GA4 이벤트 전송 실패:', error);
    }
  }
};

/**
 * 페이지뷰 추적
 * @param {string} pagePath - 페이지 경로
 * @param {string} pageTitle - 페이지 제목
 */
export const trackPageView = (pagePath, pageTitle) => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('config', GA_MEASUREMENT_ID, {
      page_path: pagePath,
      page_title: pageTitle
    });
  }
};

// ============================================================================
// 회원 관련 이벤트
// ============================================================================

/**
 * 회원가입 완료
 */
export const trackSignUp = (method = 'email') => {
  trackEvent('sign_up', {
    method: method,
    event_category: 'engagement'
  });
};

/**
 * 로그인
 */
export const trackLogin = (method = 'email') => {
  trackEvent('login', {
    method: method,
    event_category: 'engagement'
  });
};

// ============================================================================
// 결제 관련 이벤트 (GA4 전자상거래)
// ============================================================================

/**
 * 결제 페이지 진입
 */
export const trackBeginCheckout = (planType, amount) => {
  trackEvent('begin_checkout', {
    currency: 'KRW',
    value: amount,
    items: [{
      item_id: `pro_${planType}`,
      item_name: `Pro ${planType}`,
      price: amount,
      quantity: 1
    }],
    event_category: 'ecommerce'
  });
};

/**
 * 결제 정보 입력
 */
export const trackAddPaymentInfo = (paymentType = 'card') => {
  trackEvent('add_payment_info', {
    payment_type: paymentType,
    event_category: 'ecommerce'
  });
};

/**
 * 결제 완료 (구매)
 */
export const trackPurchase = (transactionId, planType, amount) => {
  trackEvent('purchase', {
    transaction_id: transactionId,
    currency: 'KRW',
    value: amount,
    items: [{
      item_id: `pro_${planType}`,
      item_name: `Pro ${planType}`,
      price: amount,
      quantity: 1
    }],
    event_category: 'ecommerce'
  });
};

/**
 * 결제 실패
 */
export const trackPurchaseFailed = (reason, planType, amount) => {
  trackEvent('purchase_failed', {
    error_reason: reason,
    currency: 'KRW',
    value: amount,
    items: [{
      item_id: `pro_${planType}`,
      item_name: `Pro ${planType}`,
      price: amount,
      quantity: 1
    }],
    event_category: 'ecommerce'
  });
};

/**
 * 환불
 */
export const trackRefund = (transactionId, amount) => {
  trackEvent('refund', {
    transaction_id: transactionId,
    currency: 'KRW',
    value: amount,
    event_category: 'ecommerce'
  });
};

// ============================================================================
// 기능 사용 이벤트
// ============================================================================

/**
 * 생기부 생성
 */
export const trackSaenggibuCreate = (category, wordCount, isPro, metadata = {}) => {
  trackEvent('saenggibu_create', {
    category: category,
    word_count: wordCount,
    is_pro: isPro,
    ...metadata,
    event_category: 'feature_usage'
  });
};

/**
 * 생기부 검사기 사용
 */
export const trackInspectorUse = (mode, schoolLevel, isPro) => {
  trackEvent('inspector_use', {
    mode: mode,  // single, batch
    school_level: schoolLevel,
    is_pro: isPro,
    event_category: 'feature_usage'
  });
};

/**
 * AI 채팅 사용
 */
export const trackChatUse = (category, model, isPro) => {
  trackEvent('chat_use', {
    category: category,
    model: model,
    is_pro: isPro,
    event_category: 'feature_usage'
  });
};

/**
 * 파일 업로드
 */
export const trackFileUpload = (fileType, feature) => {
  trackEvent('file_upload', {
    file_type: fileType,
    feature: feature,
    event_category: 'feature_usage'
  });
};

// ============================================================================
// 전환 퍼널 이벤트
// ============================================================================

/**
 * 요금제 페이지 조회
 */
export const trackViewPricing = (source = 'direct') => {
  trackEvent('view_pricing', {
    source: source,
    event_category: 'conversion_funnel'
  });
};

/**
 * Pro 업그레이드 버튼 클릭
 */
export const trackClickUpgrade = (source, planType) => {
  trackEvent('click_upgrade', {
    source: source,
    plan_type: planType,
    event_category: 'conversion_funnel'
  });
};

/**
 * 무료 체험 한도 도달
 */
export const trackFreeLimitReached = (feature) => {
  trackEvent('free_limit_reached', {
    feature: feature,
    event_category: 'conversion_funnel'
  });
};

/**
 * Pro 업그레이드 모달 표시
 */
export const trackShowUpgradeModal = (trigger, feature) => {
  trackEvent('show_upgrade_modal', {
    trigger: trigger,
    feature: feature,
    event_category: 'conversion_funnel'
  });
};

// ============================================================================
// Stickiness & Engagement 이벤트
// ============================================================================

/**
 * 세션 시작 (앱 진입)
 */
export const trackSessionStart = (userId, isPro) => {
  trackEvent('session_start', {
    user_id: userId,
    is_pro: isPro,
    entry_time: new Date().toISOString(),
    event_category: 'engagement'
  });
};

/**
 * 세션 종료 (앱 이탈)
 * @param {number} sessionDuration - 세션 지속 시간 (초)
 * @param {number} actionsCount - 세션 중 수행한 액션 수
 */
export const trackSessionEnd = (sessionDuration, actionsCount) => {
  trackEvent('session_end', {
    session_duration_seconds: sessionDuration,
    actions_count: actionsCount,
    event_category: 'engagement'
  });
};

/**
 * 기능별 사용 시간 추적
 * @param {string} feature - 기능명 (chat, saenggibu, inspector)
 * @param {number} duration - 사용 시간 (초)
 */
export const trackFeatureEngagement = (feature, duration, isPro) => {
  trackEvent('feature_engagement', {
    feature: feature,
    duration_seconds: duration,
    is_pro: isPro,
    event_category: 'engagement'
  });
};

/**
 * 일간 활성 사용자 추적 (첫 번째 세션)
 */
export const trackDailyActive = (userId) => {
  const today = new Date().toISOString().split('T')[0];
  const lastActiveKey = `lastActive_${userId}`;
  const lastActive = localStorage.getItem(lastActiveKey);
  
  if (lastActive !== today) {
    localStorage.setItem(lastActiveKey, today);
    trackEvent('daily_active_user', {
      date: today,
      event_category: 'retention'
    });
  }
};

/**
 * 재방문 추적
 * @param {number} daysSinceLastVisit - 마지막 방문 후 경과 일수
 */
export const trackReturn = (daysSinceLastVisit) => {
  trackEvent('user_return', {
    days_since_last_visit: daysSinceLastVisit,
    return_type: daysSinceLastVisit === 1 ? 'next_day' : 
                 daysSinceLastVisit <= 7 ? 'within_week' : 'after_week',
    event_category: 'retention'
  });
};

// ============================================================================
// Growth & Acquisition 이벤트
// ============================================================================

/**
 * 사용자 획득 경로 추적
 * @param {string} source - 유입 경로 (organic, referral, paid, direct)
 * @param {string} medium - 매체 (google, naver, facebook 등)
 * @param {string} campaign - 캠페인명
 */
export const trackAcquisition = (source, medium, campaign = '') => {
  trackEvent('user_acquisition', {
    traffic_source: source,
    traffic_medium: medium,
    campaign: campaign,
    event_category: 'acquisition'
  });
};

/**
 * 첫 결제까지의 퍼널 추적
 * @param {string} stage - 퍼널 단계
 */
export const trackConversionFunnel = (stage, metadata = {}) => {
  const stages = {
    'signup': 1,
    'first_feature_use': 2,
    'view_pricing': 3,
    'start_checkout': 4,
    'complete_payment': 5
  };
  
  trackEvent('conversion_funnel', {
    stage: stage,
    stage_number: stages[stage] || 0,
    ...metadata,
    event_category: 'conversion'
  });
};

/**
 * Pro 전환 추적
 */
export const trackProConversion = (conversionPath, daysToConvert, totalSessions) => {
  trackEvent('pro_conversion', {
    conversion_path: conversionPath,  // e.g., "saenggibu_limit"
    days_to_convert: daysToConvert,
    total_sessions_before_convert: totalSessions,
    event_category: 'conversion'
  });
};

// ============================================================================
// LTV & Revenue 이벤트
// ============================================================================

/**
 * 구독 갱신 추적
 */
export const trackSubscriptionRenewal = (planType, renewalCount, totalRevenue) => {
  trackEvent('subscription_renewal', {
    plan_type: planType,
    renewal_count: renewalCount,
    cumulative_revenue: totalRevenue,
    event_category: 'revenue'
  });
};

/**
 * 구독 취소/이탈 추적
 */
export const trackChurn = (reason, subscriptionDuration, totalPaid) => {
  trackEvent('subscription_churn', {
    churn_reason: reason,
    subscription_duration_days: subscriptionDuration,
    total_paid: totalPaid,
    event_category: 'revenue'
  });
};

/**
 * 업그레이드/다운그레이드 추적
 */
export const trackPlanChange = (fromPlan, toPlan, reason) => {
  trackEvent('plan_change', {
    from_plan: fromPlan,
    to_plan: toPlan,
    change_reason: reason,
    is_upgrade: toPlan === 'pro' || (fromPlan === '3month' && toPlan === '12month'),
    event_category: 'revenue'
  });
};

// ============================================================================
// 공유 링크 UTM 자동 추가
// ============================================================================

/**
 * 공유 링크에 UTM 파라미터 추가
 * @param {string} url - 기본 URL
 * @param {string} source - 공유 소스 (kakao, naver, twitter, copy 등)
 * @param {string} medium - 매체 (share, referral 등)
 * @param {string} campaign - 캠페인명 (선택)
 * @param {string} content - 콘텐츠 ID (선택)
 * @returns {string} UTM이 추가된 URL
 */
export const addUtmToShareUrl = (url, source, medium = 'share', campaign = '', content = '') => {
  try {
    const urlObj = new URL(url);
    
    // 기본 UTM 파라미터
    urlObj.searchParams.set('utm_source', source);
    urlObj.searchParams.set('utm_medium', medium);
    
    if (campaign) {
      urlObj.searchParams.set('utm_campaign', campaign);
    }
    
    if (content) {
      urlObj.searchParams.set('utm_content', content);
    }
    
    return urlObj.toString();
  } catch (error) {
    console.error('UTM 추가 실패:', error);
    return url; // 실패 시 원본 URL 반환
  }
};

/**
 * 카카오톡 공유용 UTM URL 생성
 */
export const getKakaoShareUrl = (url, campaign = 'kakao_share') => {
  return addUtmToShareUrl(url, 'kakao', 'share', campaign);
};

/**
 * 네이버 블로그 공유용 UTM URL 생성
 */
export const getNaverShareUrl = (url, campaign = 'naver_blog') => {
  return addUtmToShareUrl(url, 'naver', 'share', campaign);
};

/**
 * 트위터/X 공유용 UTM URL 생성
 */
export const getTwitterShareUrl = (url, campaign = 'twitter_share') => {
  return addUtmToShareUrl(url, 'twitter', 'share', campaign);
};

/**
 * 일반 복사 공유용 UTM URL 생성
 */
export const getCopyShareUrl = (url, campaign = 'copy_share') => {
  return addUtmToShareUrl(url, 'copy', 'share', campaign);
};

/**
 * 추천 링크용 UTM URL 생성
 */
export const getReferralShareUrl = (url, referralCode, campaign = 'referral') => {
  return addUtmToShareUrl(url, 'referral', 'referral', campaign, referralCode);
};

// ============================================================================
// 사용자 속성 설정
// ============================================================================

/**
 * 사용자 속성 설정
 */
export const setUserProperties = (userId, properties = {}) => {
  if (typeof window !== 'undefined' && window.gtag) {
    // 사용자 ID 설정
    window.gtag('config', GA_MEASUREMENT_ID, {
      user_id: userId
    });
    
    // 사용자 속성 설정
    window.gtag('set', 'user_properties', {
      subscription_type: properties.subscriptionType || 'free',
      is_pro: properties.isPro || false,
      signup_date: properties.signupDate,
      days_since_signup: properties.daysSinceSignup,
      total_sessions: properties.totalSessions,
      features_used: properties.featuresUsed,
      ...properties
    });
  }
};

// 기본 export
const analytics = {
  // UTM 관리
  initUtmTracking,
  extractUtmFromUrl,
  saveUtmToStorage,
  getUtmFromStorage,
  saveFirstUtm,
  getFirstUtmFromStorage,
  trackUtmVisit,
  // 공유 링크 UTM
  addUtmToShareUrl,
  getKakaoShareUrl,
  getNaverShareUrl,
  getTwitterShareUrl,
  getCopyShareUrl,
  getReferralShareUrl,
  // 기본 이벤트
  trackEvent,
  trackPageView,
  trackSignUp,
  trackLogin,
  trackBeginCheckout,
  trackAddPaymentInfo,
  trackPurchase,
  trackPurchaseFailed,
  trackRefund,
  trackSaenggibuCreate,
  trackInspectorUse,
  trackChatUse,
  trackFileUpload,
  trackViewPricing,
  trackClickUpgrade,
  trackFreeLimitReached,
  trackShowUpgradeModal,
  trackConversionFunnel,
  trackProConversion,
  trackSessionStart,
  trackSessionEnd,
  trackFeatureEngagement,
  trackDailyActive,
  trackReturn,
  trackAcquisition,
  trackSubscriptionRenewal,
  trackChurn,
  trackPlanChange,
  setUserProperties
};

export default analytics;

